import { useState } from 'react';

import { usePagination } from '../../../common/hooks/usePagination';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useMaterialCostLayers } from '../../../hooks/useMaterialCosts';
import { useMaterialReports } from '../../../hooks/useMaterialReports';
import { MATERIAL_HISTORY_VALUATION_BASIS } from '../constants/inventoryArticlesConstants';

const toSection = (query, pagination, tableSort) => ({
  rows: query.data?.items,
  total: query.data?.total,
  ...pagination,
  sort: tableSort.sort,
  onSort: tableSort.toggleSort,
  isLoading: query.isLoading,
  errorMessage: query.isError ? query.error?.response?.data?.message || '' : null,
});

/**
 * Historia de costos de un artículo: capas FIFO y compras, cada una con su paginación y su orden.
 * Al cambiar de artículo vuelve a la página 1 y limpia el orden de ambas tablas.
 * @param {string} articleId UUID del artículo.
 * @param {boolean} enabled Habilita las consultas.
 * @returns {{ layers: Object, purchases: Object }}
 */
export const useMaterialArticleHistory = (articleId, enabled) => {
  const layersPagination = usePagination();
  const purchasesPagination = usePagination();
  const { page: layersPage, setPage: setLayersPage } = layersPagination;
  const { page: purchasesPage, setPage: setPurchasesPage } = purchasesPagination;
  const [historyArticleId, setHistoryArticleId] = useState(articleId);
  const layersSort = useTableSort(() => setLayersPage(1));
  const purchasesSort = useTableSort(() => setPurchasesPage(1));
  const isEnabled = Boolean(enabled && articleId);

  if (historyArticleId !== articleId) {
    setHistoryArticleId(articleId);
    setLayersPage(1);
    setPurchasesPage(1);
    layersSort.resetSort();
    purchasesSort.resetSort();
  }

  const layers = useMaterialCostLayers(
    {
      uuid_inventory_article: articleId,
      page: layersPage,
      per_page: layersPagination.rowsPerPage,
      ...layersSort.sortParams,
    },
    { enabled: isEnabled }
  );
  const purchases = useMaterialReports(
    'article-costs',
    {
      uuid_inventory_article: articleId,
      valuation_basis: MATERIAL_HISTORY_VALUATION_BASIS,
      page: purchasesPage,
      per_page: purchasesPagination.rowsPerPage,
      ...purchasesSort.sortParams,
    },
    { enabled: isEnabled }
  );

  return {
    layers: toSection(layers, layersPagination, layersSort),
    purchases: toSection(purchases, purchasesPagination, purchasesSort),
  };
};
